/**
 * Minimal JWT helpers. We only need to read the `exp` claim to schedule
 * refreshes — full verification happens server-side on care-management-gql.
 */

interface JwtClaims {
  exp?: number;
  /** Entra v1 object id claim — present on Entra tokens, absent on B2C. */
  oid?: string;
  /** Standard subject claim — present on all tokens; used as OID fallback. */
  sub?: string;
}

function base64UrlDecode(segment: string): string | null {
  try {
    const padded = segment.replace(/-/g, "+").replace(/_/g, "/");
    return atob(padded);
  } catch {
    return null;
  }
}

export function decodeJwtClaims(token: string): JwtClaims | null {
  const parts = token.split(".");
  if (parts.length < 2) return null;
  const payload = base64UrlDecode(parts[1]);
  if (payload === null) return null;
  try {
    return JSON.parse(payload) as JwtClaims;
  } catch {
    return null;
  }
}

/** Returns the `exp` claim in epoch milliseconds, or null if missing/malformed. */
export function jwtExpiryMs(token: string | null | undefined): number | null {
  if (!token) return null;
  const claims = decodeJwtClaims(token);
  if (!claims?.exp) return null;
  return claims.exp * 1000;
}

/**
 * Extract a stable, non-PHI user identifier from an access token for telemetry.
 * Prefers the Entra `oid` claim (opaque object id); falls back to `sub`.
 * Returns null when the token is absent or malformed.
 */
export function jwtUserOid(token: string | null | undefined): string | null {
  if (!token) return null;
  const claims = decodeJwtClaims(token);
  if (!claims) return null;
  return claims.oid ?? claims.sub ?? null;
}

/** True if the token is within `withinMs` of expiring (or already has). */
export function isJwtExpiringSoon(
  token: string | null | undefined,
  withinMs: number,
  now: number = Date.now(),
): boolean {
  const expMs = jwtExpiryMs(token);
  if (expMs === null) return false;
  return expMs - now <= withinMs;
}
